"use client";
import { createContext, useContext, useEffect, useState } from "react";
import {
  AdminPanelContextType,
  AdminPanelContextState,
  AdminPanelTab,
} from "./types";
import {
  addNote,
  finalizeFolio,
  getNotes,
  Note,
  updateNote,
  getUser,
  getFolio,
} from "@csc-dashboard/api-client";
import { AlertProvider, useAlert } from "../alert";
import { AtTagFolioStatus } from "@csc-dashboard/components-library";

export const AdminPanelContext = createContext<
  AdminPanelContextType | undefined
>(undefined);

const AdminPanelProviderInner = ({
  children,
  initialState,
}: {
  children: React.ReactNode;
  initialState: AdminPanelContextState;
}) => {
  // State management with useState hooks
  const [hideAlert, setHideAlert] = useState(initialState.hideAlert);
  const [currentTab, setCurrentTabState] = useState<AdminPanelTab>("client");
  const [tabParams, setTabParams] = useState<
    Record<string, unknown> | undefined
  >();
  const [foliosPage, setFoliosPage] = useState<number>(0);
  const [isForbidden, setIsForbidden] = useState<boolean>(false);
  const [agentName, setAgentName] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notes, setNotes] = useState<Note[]>([]);
  const { showError, showSuccess } = useAlert();
  const [folioStatus, setFolioStatus] = useState<AtTagFolioStatus>();

  const isGuest = initialState.isGuest ?? false;

  const setCurrentTab = (
    tab: AdminPanelTab,
    params?: Record<string, unknown>,
  ) => {
    setCurrentTabState(tab);
    setTabParams(params);
  };

  const removeAlert = () => {
    setHideAlert(true);
  };

  const togglePanel = () => setIsOpen(!isOpen);
  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const closeAttention = async () => {
    try {
      setIsLoading(true);
      if (folioStatus !== AtTagFolioStatus.CLOSED) {
        await finalizeFolio({ folioId: initialState.folioId });
      }
      showSuccess(`Atención finalizada`);
      toggleModal();
      togglePanel();
      initialState.onAttentionFinished?.();
    } catch (error) {
      showError("Error al finalizar la atención");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const createNote = async (params: { noteId?: string; noteBody?: string }) => {
    try {
      const text = params.noteBody || "";
      setIsLoading(true);
      const { note } = await addNote({ folioId: initialState.folioId, text });
      setNotes([...notes, { ...note, title: `Nota #${notes.length + 1}` }]);
      showSuccess("Nota creada correctamente");
      setCurrentTab("notes");
    } catch (error) {
      showError("Error al crear la nota");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const editNote = async (params: { noteId?: string; noteBody?: string }) => {
    try {
      const updateParams = {
        folioId: initialState.folioId,
        noteId: params.noteId || "",
        text: params.noteBody || "",
      };
      setIsLoading(true);
      const { note: updatedNote } = await updateNote(updateParams);
      setNotes(
        notes.map((n) =>
          n.id === params.noteId
            ? { ...updatedNote, title: `Nota #${notes.indexOf(n) + 1}` }
            : n,
        ),
      );
      showSuccess("Nota editada correctamente");
      setCurrentTab("notes");
    } catch (error) {
      showError("Error al editar la nota");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      const { notes } = await getNotes({ folioId: initialState.folioId });
      setNotes(notes);
    } catch (error) {
      showError("Error al obtener las notas");
      throw error;
    }
  };

  const getAgentInfo = async () => {
    try {
      const AgentInfo = await getUser();
      setAgentName(AgentInfo.firstName + " " + AgentInfo.lastName);
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any).status === 403 || (error as any).statusCode === 403)
        setIsForbidden(true);
      showError("Error al obtener la información del agente");
      throw error;
    }
  };

  const getFolioStatus = async () => {
    try {
      const { folio } = await getFolio({ id: initialState.folioId });
      setFolioStatus(folio.status.toLowerCase() as AtTagFolioStatus);
    } catch (error) {
      showError("Error al obtener el estado del folio");
      throw error;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      Promise.all([fetchNotes(), getAgentInfo(), getFolioStatus()]).finally(
        () => {
          setIsLoading(false);
        },
      );
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AdminPanelContextType = {
    hideAlert,
    isloadingUserInfo: initialState.isloadingUserInfo,
    userInfo: initialState.userInfo,
    agentName,
    folioId: initialState.folioId,
    folioStatus,
    onAttentionFinished: () => initialState.onAttentionFinished?.(),
    setFoliosPage,
    foliosPage,
    currentTab,
    tabParams,
    isOpen,
    isGuest,
    notes,
    createNote,
    editNote,
    isModalOpen,
    isLoading,
    isForbidden,
    toggleModal,
    togglePanel,
    removeAlert,
    setCurrentTab,
    closeAttention,
  };

  return (
    <AdminPanelContext.Provider value={value}>
      {children}
    </AdminPanelContext.Provider>
  );
};

export const AdminPanelProvider = ({
  children,
  initialState,
}: {
  children: React.ReactNode;
  initialState: AdminPanelContextState;
}) => {
  return (
    <div className="csc-admin-panel">
      <AlertProvider>
        <AdminPanelProviderInner initialState={initialState}>
          {children}
        </AdminPanelProviderInner>
      </AlertProvider>
    </div>
  );
};

export const useAdminPanel = () => {
  const context = useContext(AdminPanelContext);
  if (context === undefined) {
    throw new Error("useAdminPanel must be used within an AdminPanelProvider");
  }
  return context;
};
